import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Party, PartyInsert, PartyUpdate } from '../models/party';
import { catchError, lastValueFrom, map, of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { plainToInstance } from 'class-transformer';
import { Parties } from '../state/parties/party-actions';
import { AddressInsert, Address, AddressUpdate } from '../models/address';
import { EmailInsert, Email, EmailUpdate } from '../models/email';
import { ErrorMessage } from '../models/errors';
import { PhoneInsert, Phone, PhoneUpdate } from '../models/phone';
import { ApiService } from './api.service';
import { BASE_URL } from './injection-tokens';

export abstract class PartyServiceBase
{
    abstract createNewParty(party: PartyInsert): Promise<Party | undefined>;
    abstract updateParty(partyId: number, party: PartyUpdate) : Promise<Party>
    abstract searchParties(q: string): Promise<Party[]>
    abstract getParty(id: number) : Promise<Party>

    abstract addAddress(partyId: number, address: AddressInsert) : Promise<Address | undefined>
    abstract addEmail(partyId: number, email: EmailInsert) : Promise<Email>
    abstract addPhone(partyId: number, phone: PhoneInsert) : Promise<Phone>
    abstract updateAddress(partyId: number, addressId: number, address: AddressUpdate) : Promise<Address>
    abstract updateEmail(partyId: number, emailId: number, email: EmailUpdate) : Promise<Email>
    abstract updatePhone(partyId: number, phoneId: number, phone: PhoneUpdate) : Promise<Phone>
    abstract deleteAddress(partyId: number, addressId: number) : Promise<Address>
    abstract deleteEmail(partyId: number, emailId: number): Promise<Email>
    abstract deletePhone(partyId: number, phoneId: number) : Promise<Phone>

}



@Injectable({
  providedIn: 'root'
})
export class PartyService extends PartyServiceBase
{


  override getParty(id: number): Promise<Party> 
  {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<Party>(this._baseUri, "/parties/" + id.toString())
          .pipe(
            map(p => plainToInstance(Party, p))
          )
      );
  }

  

  override createNewParty(party: PartyInsert): Promise<Party | undefined> 
  {
    return lastValueFrom
          (
            this._api.makeAPIPutCall<Party>(this._baseUri,"/parties",party,{})
                  .pipe
                  (
                    map(p => { this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Created Party ' + p.firstName + ' ' + p.lastName}); return plainToInstance(Party,p)} )
                    ,catchError((error:ErrorMessage) =>  {this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Create Party', detail: ' ' + error.message}); return of(undefined)})

                  )
          );
  }

  override updateParty(partyId: number, party: PartyUpdate): Promise<Party> 
  {
    return lastValueFrom
    (
      this._api.makeAPIPatchCall<Party>(this._baseUri,"/parties/" + partyId.toString(), party, {})
      .pipe
      (
        map
        (
          p => 
            {
              this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Updated Party ' + p.firstName + ' ' + p.lastName});
              this._store.dispatch(new Parties.PartyServicePartyUpdated({party:p}));

              return p;
            }
        )
      )
    );
  }
  override searchParties(q: string): Promise<Party[]> 
  {
    return lastValueFrom
          (
            this._api.makeAPIGetCall<Party[]>(this._baseUri,"/parties",{query: q})
                    .pipe(map(parties => parties.map(p => new Party(p.id,p.firstName,p.lastName,new Date(p.dateOfBirth),p.middleInitial))))
          );
  }


  override addAddress(partyId: number, address: AddressInsert): Promise<Address | undefined> 
  {
    return lastValueFrom
          (
            this._api.makeAPIPutCall<Address>(this._baseUri,"/parties/" + partyId.toString() + "/address",address)
            .pipe
            (
              map
              (
                a => 
                  {
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Address Created'});
                    this._store.dispatch(new Parties.PartyServiceAddressAdded({partyId:partyId,address:a}))
                    return a;
                  }
              ),
              catchError((error:ErrorMessage) =>  {this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Add Address', detail: ' ' + error.message}); return of(undefined)})
            )
          );
  }

  override addEmail(partyId: number, email: EmailInsert): Promise<Email> 
  {
    return lastValueFrom
          (
            this._api.makeAPIPutCall<Email>(this._baseUri,"/parties/" + partyId.toString() + "/email",email)
            .pipe
            (
              map
              (
                e => 
                  {
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Email Created'});
                    this._store.dispatch(new Parties.PartyServiceEmailAdded({partyId:partyId,email:e}))
                    return e;
                  }
              )
            )
          );
  }

  override addPhone(partyId: number, phone: PhoneInsert): Promise<Phone> 
  {
    return lastValueFrom
          (
            this._api.makeAPIPutCall<Phone>(this._baseUri,"/parties/" + partyId.toString() + "/phone",phone)
            .pipe
            (
              map
              (
                p => 
                  {
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Phone Created'});
                    this._store.dispatch(new Parties.PartyServicePhoneAdded({partyId:partyId,phone:p}))
                    return p;
                  }
              )
            )
          );
    }

  override updateAddress(partyId: number, addressId: number, address: AddressUpdate): Promise<Address> 
  {
    return lastValueFrom
        (
          this._api.makeAPIPatchCall<Address>(this._baseUri,"/parties/"+partyId.toString() + "/address/" + addressId.toString(),address)
          .pipe
            (
              map
              (
                a => 
                  {
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Address Updated'});
                    this._store.dispatch(new Parties.PartyServiceAddressUpdated({partyId:partyId,address:a}))
                    return a;
                  }
              )
            )
        )
  }



  override updateEmail(partyId: number, emailId: number, email: EmailUpdate): Promise<Email> 
  {
    return lastValueFrom
    (
      this._api.makeAPIPatchCall<Email>(this._baseUri,"/parties/"+partyId.toString() + "/email/" + emailId.toString(),email)
      .pipe
        (
          map
          (
            e => 
              {
                this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Email Updated'});
                this._store.dispatch(new Parties.PartyServiceEmailUpdated({partyId:partyId,email:e}))
                return e;
              }
          )
        )
    )
  }
  override updatePhone(partyId: number, phoneId: number, phone: PhoneUpdate): Promise<Phone> 
  {
    return lastValueFrom
        (
          this._api.makeAPIPatchCall<Phone>(this._baseUri,"/parties/"+partyId.toString() + "/phone/" + phoneId.toString(),phone)
          .pipe
            (
              map
              (
                p => 
                  {
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Phone Updated'});
                    this._store.dispatch(new Parties.PartyServicePhoneUpdated({partyId:partyId,phone:p}))
                    return p;
                  }
              )
            )
        )
  }

  override deleteAddress(partyId: number, addressId: number): Promise<Address> 
  {
    return lastValueFrom
          (
            this._api.makeAPIDeleteCall<Address>(this._baseUri,"/parties/"+partyId.toString() + "/address/" + addressId.toString())
            .pipe
            (
              map
              (
                a => 
                  {
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Address Deleted'});
                    this._store.dispatch(new Parties.PartyServiceAddressDeleted({partyId:partyId,address:a}))
                    return a;
                  }
              )
            )
          )
  }

  override deleteEmail(partyId: number, emailId: number): Promise<Email> 
  {
    return lastValueFrom
          (
            this._api.makeAPIDeleteCall<Email>(this._baseUri,"/parties/"+partyId.toString() + "/email/" + emailId.toString())
            .pipe
            (
              map
              (
                e => 
                  {
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Email Deleted'});
                    this._store.dispatch(new Parties.PartyServiceEmailDeleted({partyId:partyId,email:e}))
                    return e;
                  }
              )
            )
          )
  }
  override deletePhone(partyId: number, phoneId: number): Promise<Phone> 
  {
    return lastValueFrom
          (
            this._api.makeAPIDeleteCall<Phone>(this._baseUri,"/parties/"+partyId.toString() + "/phone/" + phoneId.toString())
            .pipe
            (
              map
              (
                p => 
                  {
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Phone Deleted'});
                    this._store.dispatch(new Parties.PartyServicePhoneDeleted({partyId:partyId,phone:p}))
                    return p;
                  }
              )
            )
          )
  }



  constructor
  (
    private _api: ApiService,
    private _messages: MessageService,
    private _store: Store,
    @Inject(BASE_URL) private _baseUri: string,
  )
  {
    super();
  }



}